import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleNotch, Envelope, LockKey, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PasswordInput } from "@/components/PasswordInput";
import { API } from "@/lib/api";

const BG_IMAGE =
  "https://images.unsplash.com/photo-1631812314418-d2c27f4d85ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhbWFsZmklMjBjb2FzdCUyMHN1bnNldHxlbnwwfHx8fDE3NzY5ODg2NTR8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      // res won't have the user directly but we rely on navigate; login already sets context
      // we'll route based on email heuristic is wrong—best to wait a tick and let context redirect
      navigate("/");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  // Surface API mismatch banner only when the bundled API host differs from the
  // host serving this page. Hidden during normal operation; visible the moment
  // a custom-domain deploy is wired up but the frontend bundle still points at
  // the preview URL.
  let apiHost = "";
  let apiMismatch = false;
  if (typeof window !== "undefined") {
    try {
      apiHost = new URL(API).host;
      apiMismatch = apiHost !== window.location.host;
    } catch {
      apiMismatch = false;
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-ink text-white grain">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={BG_IMAGE} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/70 to-black/90" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="p-8">
          <Logo size="default" />
        </div>

        <div className="flex-1 flex items-center justify-center px-6">
          <div className="w-full max-w-md">
            <div className="mb-10">
              <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-4 font-sans">
                Agent Portal
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-white mb-3">
                Sign in to your <span className="italic text-[#D4AF37]">studio</span>
              </h1>
              <p className="text-white/60 text-sm font-sans">
                Craft unforgettable journeys for your clients.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
              {apiMismatch && (
                <div
                  className="flex items-start gap-2 p-3 rounded-md border border-amber-400/50 bg-amber-500/10 text-amber-200"
                  data-testid="login-api-mismatch-banner"
                >
                  <WarningCircle size={16} weight="duotone" className="mt-0.5 shrink-0" />
                  <p className="text-[11px] font-sans leading-relaxed">
                    Frontend bundled with API host <strong>{apiHost}</strong>, but you're on <strong>{window.location.host}</strong>.
                    Login requests are crossing origins — please ask the platform admin to redeploy with the correct <code>REACT_APP_BACKEND_URL</code>.
                  </p>
                </div>
              )}
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                  Email
                </Label>
                <div className="relative">
                  <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" weight="duotone" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="pl-10 h-12 bg-black/40 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 font-sans"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                  Password
                </Label>
                <PasswordInput
                  icon={LockKey}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#D4AF37] hover:bg-[#F3C853] text-black font-medium text-sm uppercase tracking-[0.25em] transition-all duration-200 hover:-translate-y-[1px]"
                data-testid="login-submit-btn"
              >
                {loading ? <CircleNotch size={18} className="animate-spin" /> : "Enter Portal"}
              </Button>

              <div className="text-center pt-1 space-y-2">
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password-link"
                  className="block text-[11px] uppercase tracking-[0.3em] text-white/50 hover:text-[#D4AF37] transition-colors font-sans"
                >
                  Forgot password?
                </Link>
                <Link
                  to="/register"
                  data-testid="login-register-link"
                  className="block text-[11px] uppercase tracking-[0.3em] text-white/50 hover:text-[#D4AF37] transition-colors font-sans"
                >
                  New agent? Create an account
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className="p-6 text-center text-[11px] uppercase tracking-[0.3em] text-white/30 font-sans">
          A curated concierge experience
        </div>
      </div>
    </div>
  );
}
